import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Phone, Mail, FileText, CheckCircle2, AlertTriangle,
  ChevronRight, ExternalLink, Clock, RefreshCw, Banknote, XCircle, Scale,
  MessageSquare, Plus, CreditCard, Trash2, Link2, Copy, Bell, X, Pencil
} from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import PolishNoteButton from "../components/PolishNoteButton";
import EmailDeliveryAudit from "../components/EmailDeliveryAudit";
import EditCreditControlModal from "../components/EditCreditControlModal";
import { API } from "../config/api";
import { getUserRole, ROLES, FEE_EARNERS } from "../config/roleConfig";
import { parseBlobError } from "../utils/parseBlobError";
import { staffNotifyCandidates } from "../lib/staffNotifyCandidates";

const STAGES_ORDER = [
  "invoice_issued", "reminder_1", "call_1", "reminder_2",
  "call_2", "reminder_3", "call_3", "lba_sent", "proceedings",
];
const STAGE_LABELS = {
  invoice_issued: "Invoice", reminder_1: "R1", call_1: "C1",
  reminder_2: "R2", call_2: "C2", reminder_3: "R3",
  call_3: "C3", lba_sent: "LBA", proceedings: "Proc",
};

const CALL_OUTCOMES = [
  { value: "paid", label: "Paid" },
  { value: "promise_of_payment", label: "Promise of payment + date" },
  { value: "no_answer", label: "No answer" },
  { value: "voicemail_left", label: "Voicemail left" },
  { value: "query_raised", label: "Query raised" },
  { value: "dispute_flagged", label: "Dispute flagged" },
  { value: "partial_payment_offered", label: "Partial payment offered" },
  { value: "payment_arrangement_agreed", label: "Payment arrangement agreed" },
  { value: "lba_warning_given", label: "LBA warning given" },
  { value: "referred_to_director", label: "Referred to director" },
];

/** The signed-in user's own email address, as the backend will see it. */
function currentUserEmail() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return (
      user.user_email || user.email || user.ms_email ||
      localStorage.getItem("bonsai_user_email") || ""
    );
  } catch {
    return localStorage.getItem("bonsai_user_email") || "";
  }
}

function headers() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const email = user.user_email || user.email || user.ms_email || localStorage.getItem("bonsai_user_email") || "";
  return {
    "X-Bonsai-User-Id": localStorage.getItem("bonsai_user_id") || "",
    "X-User-Email": email,
  };
}

/** Normalise any log item to a sortable ISO timestamp string. */
function itemTimestamp(item) {
  return item.logged_at || item.recorded_at || item.sent_at || item.date || item.created_at || "";
}

function EscalationTracker({ currentStage }) {
  const idx = STAGES_ORDER.indexOf(currentStage);
  return (
    <div className="flex items-center gap-1 overflow-x-auto py-2" data-testid="escalation-tracker">
      {STAGES_ORDER.map((stage, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <div key={stage} className="flex items-center">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold shrink-0
              ${done ? "bg-teal text-black" : active ? "bg-status-amber text-white ring-2 ring-orange-400/40" : "bg-white/10 text-gray-500"}`}>
              {done ? <CheckCircle2 className="w-4 h-4" /> : STAGE_LABELS[stage]}
            </div>
            {i < STAGES_ORDER.length - 1 && (
              <div className={`w-4 h-0.5 ${i < idx ? "bg-teal" : "bg-white/10"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function CallLogModal({ open, onClose, onSaved, record }) {
  const [form, setForm] = useState({ outcome: "no_answer", notes: "", promise_date: new Date().toISOString().split("T")[0], generate_pdf: true });
  const [saving, setSaving] = useState(false);

  if (!open) return null;
  const callNum = (record?.call_log?.length || 0) + 1;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/credit-control/${record.id}/log-call`, form, { headers: headers() });
      onSaved();
      onClose();
    } catch (err) {
      console.error("Log call failed:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="log-call-modal">
      <div className="bg-navy-card border border-white/10 rounded-xl p-6 w-full max-w-lg">
        <h3 className="text-lg font-bold text-white mb-1">
          Log Call — {record.client_name}
        </h3>
        <p className="text-xs text-gray-400 mb-4 font-mono">{record.matter_ref} — Call {callNum}</p>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Outcome</label>
            <select
              value={form.outcome}
              onChange={e => setForm(p => ({ ...p, outcome: e.target.value }))}
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
              data-testid="call-outcome"
            >
              {CALL_OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              rows={4}
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white resize-none"
              placeholder="Free text — no character limit"
              data-testid="call-notes"
            />
            <div className="mt-2 flex justify-end">
              <PolishNoteButton
                rawNote={form.notes}
                onPolished={(polished) => setForm(p => ({ ...p, notes: polished }))}
                matterRef={record?.matter_ref || null}
                clientName={record?.client_name || null}
                disabled={saving}
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Promise date (if applicable)</label>
            <input
              type="date"
              value={form.promise_date}
              onChange={e => setForm(p => ({ ...p, promise_date: e.target.value }))}
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
              data-testid="call-promise-date"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={form.generate_pdf}
              onChange={e => setForm(p => ({ ...p, generate_pdf: e.target.checked }))}
              className="rounded"
            />
            Generate PDF attendance note
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
          <Button variant="outline" onClick={() => { setForm(p => ({ ...p, generate_pdf: false })); handleSave(); }}
            className="text-gray-300 border-white/20" disabled={saving}>
            Save Only
          </Button>
          <Button onClick={handleSave} disabled={saving}
            className="bg-teal text-black hover:bg-teal-hover" data-testid="call-save-pdf">
            {saving ? "Saving..." : "Save & Generate PDF"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── BM-H124: file-to-base64 helper ────────────────────────────────
// (Kept alongside AddInvoiceModal so it can attach a PDF when May keys
// a LawBillity-originated invoice into an existing chase record.)
const MAX_INV_PDF_BYTES = 15 * 1024 * 1024;

async function _invFileToBase64(file) {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = "";
  const CHUNK = 32 * 1024;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(
      null,
      // eslint-disable-next-line prefer-spread
      bytes.subarray(i, Math.min(i + CHUNK, bytes.length)),
    );
  }
  return btoa(bin);
}

function AddInvoiceModal({ open, onClose, onSaved, recordId }) {
  const [form, setForm] = useState({ invoice_number: "", amount_outstanding: "", due_date: "" });
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfError, setPdfError] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");

  if (!open) return null;

  const handleFilePick = (evt) => {
    setPdfError("");
    const file = evt.target.files && evt.target.files[0];
    if (!file) { setPdfFile(null); return; }
    if (file.size > MAX_INV_PDF_BYTES) {
      setPdfError("File too large. Maximum 15 MB.");
      setPdfFile(null);
      evt.target.value = "";
      return;
    }
    setPdfFile(file);
  };

  const handleSave = async () => {
    setSaving(true);
    setSubmitError("");
    try {
      const payload = {
        invoice_number:     form.invoice_number,
        amount_outstanding: parseFloat(form.amount_outstanding) || 0,
        due_date:           form.due_date,
        // BM-H124: everything routed through this modal is a manual
        // LawBillity entry (the CaseMinder payment-request flow drops
        // records into a different code path with origin="caseminder").
        origin:             "lawbillity",
      };
      if (pdfFile) {
        payload.invoice_pdf_b64          = await _invFileToBase64(pdfFile);
        payload.invoice_pdf_filename     = pdfFile.name;
        payload.invoice_pdf_content_type = pdfFile.type || "application/pdf";
      }

      await axios.post(
        `${API}/credit-control/${recordId}/upload-invoice`,
        payload,
        { headers: headers() },
      );
      onSaved();
      onClose();
      setForm({ invoice_number: "", amount_outstanding: "", due_date: "" });
      setPdfFile(null);
    } catch (err) {
      console.error("Upload invoice failed:", err);
      setSubmitError(errorMessage(err, "Failed to add invoice."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="add-invoice-modal">
      <div className="bg-navy-card border border-white/10 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-white mb-4">Add Invoice</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Invoice Number</label>
            <input type="text" value={form.invoice_number}
              onChange={e => setForm(p => ({ ...p, invoice_number: e.target.value }))}
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
              placeholder="INV-2026-042" data-testid="inv-number" />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Amount Outstanding (£)</label>
            <input type="number" step="0.01" value={form.amount_outstanding}
              onChange={e => setForm(p => ({ ...p, amount_outstanding: e.target.value }))}
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
              placeholder="1500.00" data-testid="inv-amount" />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Due Date</label>
            <input type="date" value={form.due_date}
              onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))}
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
              data-testid="inv-due-date" />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">
              Invoice PDF (optional, max 15 MB)
            </label>
            <input
              type="file" accept="application/pdf,image/*"
              onChange={handleFilePick}
              className="block w-full text-xs text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-teal/20 file:text-teal file:cursor-pointer"
              data-testid="inv-pdf"
            />
            {pdfFile && (
              <p className="mt-1 text-xs text-gray-400 flex items-center gap-1">
                <FileText className="w-3 h-3" />{pdfFile.name}
              </p>
            )}
            {pdfError && <p className="mt-1 text-xs text-status-red">{pdfError}</p>}
          </div>
          {submitError && (
            <p className="text-xs text-status-red" data-testid="inv-submit-error">
              {submitError}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !form.invoice_number}
            className="bg-teal text-black hover:bg-teal-hover" data-testid="inv-save">
            {saving ? "Saving..." : "Add Invoice"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SendChaserModal({ open, onClose, onSent, record }) {
  // BM-H546: the mailbox this chaser will be sent from (the signed-in user's).
  const senderEmail = currentUserEmail();
  const [template, setTemplate] = useState("reminder_1");
  const [toEmail, setToEmail] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [preview, setPreview] = useState(null);
  const [customBody, setCustomBody] = useState("");
  const [attachInvoice, setAttachInvoice] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (!open || !record) return;
    setLoadingPreview(true);
    axios.get(`${API}/credit-control/${record.id}/preview-email`, {
      params: { template }, headers: headers(),
    }).then(r => { setPreview(r.data); setCustomBody(r.data?.body || ""); }).catch(err => {
      // BM-H151 (finance audit P1-2): surface preview failures — the
      // modal used to silently show "No preview available" for any
      // error, so if the endpoint was down or 403'ing the user had no
      // hint that anything was wrong.
      console.error("Preview chaser failed:", err);
      setPreview(null);
      setCustomBody("");
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "unknown error";
      if (window.errorMessage) {
        window.errorMessage(`Chaser preview failed to load: ${detail}`);
      }
    }).finally(() => setLoadingPreview(false));
  }, [open, template, record]);

  if (!open) return null;

  const handleSend = async () => {
    if (!toEmail) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/credit-control/${record.id}/send-chaser`, {
        template, to_email: toEmail, cc_email: ccEmail,
        custom_body: customBody, attach_invoice: attachInvoice,
      }, { headers: headers() });
      // BM-H546: the chaser is sent AS the signed-in user, so the copy lands in
      // their own Outlook Sent Items. If Graph refused the send-as we still
      // sent it from the system mailbox — say so, because otherwise they go
      // looking in Sent Items again and find nothing again.
      if (res?.data?.sender_fallback) {
        const msg =
          "Chaser sent, but not from your mailbox — it went from " +
          (res.data.from_mailbox || "the system mailbox") +
          ", so it will not appear in your Outlook Sent Items. " +
          // BM-H551: the client's reply still reaches the sender because the
          // send carries Reply-To, so say that rather than leaving May to
          // assume a reply lands in an unmonitored mailbox.
          "A reply from the client will still come to " +
          (res.data.reply_to || senderEmail || "you") + ".";
        if (window.errorMessage) window.errorMessage(msg); else alert(msg);
      }
      onSent();
      onClose();
    } catch (err) {
      // BM-H151 (finance audit P1-2): surface the failure — previously
      // this only console.error'd and the modal closed as if the send
      // had gone through, so May thought the chaser was sent when it
      // wasn't. Now we alert with the backend detail (401/403/502) so
      // May can act on the actual failure mode.
      console.error("Send chaser failed:", err);
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "unknown error";
      if (window.errorMessage) {
        window.errorMessage(`Send chaser failed: ${detail}`);
      } else {
        alert(`Send chaser failed: ${detail}`);
      }
    } finally {
      setSending(false);
    }
  };

  const TEMPLATES = [
    { value: "reminder_1", label: "Day 7 — Gentle Reminder" },
    { value: "reminder_2", label: "Day 14 — Second Reminder" },
    { value: "reminder_3", label: "Day 21 — Urgent Notice" },
    { value: "lba", label: "Day 28 — Final Demand (LBA)" },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="send-chaser-modal">
      <div className="bg-navy-card border border-white/10 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-white mb-1">Send Chaser Email</h3>
        <p className="text-xs text-gray-400 mb-1">{record.client_name} — {record.matter_ref}</p>
        {/* BM-H546: sent from the signed-in user's own mailbox and saved to
            their Outlook Sent Items. Said out loud in the modal so it is
            obvious where to find the copy afterwards. */}
        {senderEmail && (
          <p className="text-xs text-gray-400 mb-4" data-testid="chaser-sender-note">
            Sends from {senderEmail} and saves to your Outlook Sent Items
          </p>
        )}
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Template</label>
            <select value={template} onChange={e => setTemplate(e.target.value)}
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="chaser-template">
              {TEMPLATES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 block mb-1">To</label>
              <input type="email" value={toEmail} onChange={e => setToEmail(e.target.value)}
                placeholder="client@example.com"
                className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="chaser-to" />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">CC (optional)</label>
              <input type="email" value={ccEmail} onChange={e => setCcEmail(e.target.value)}
                placeholder="fee-earner@bonsai.law"
                className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white" data-testid="chaser-cc" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Subject</label>
            <div className="bg-navy border border-white/10 rounded px-3 py-2 text-xs text-gray-300">
              {loadingPreview ? "Loading..." : preview ? preview.subject : "No preview available"}
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Body</label>
            {loadingPreview ? (
              <div className="bg-navy border border-white/10 rounded p-3">
                <p className="text-xs text-gray-500">Loading preview...</p>
              </div>
            ) : (
              <textarea
                value={customBody}
                onChange={e => setCustomBody(e.target.value)}
                rows={10}
                className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-xs text-gray-300 resize-none font-sans"
                data-testid="chaser-body"
              />
            )}
          </div>
          {(record.invoices || []).some(inv => inv.filename) && (
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={attachInvoice}
                onChange={e => setAttachInvoice(e.target.checked)}
                className="rounded"
                data-testid="chaser-attach-invoice"
              />
              Attach invoice PDF
            </label>
          )}
        </div>
        <div className="flex justify-between items-center mt-5">
          {/* BM-H551: the real sending mailbox, not a hardcoded name. This
              footer named one person's mailbox for every user, which was wrong
              for everyone else and wrong again when the send-as is refused. */}
          <p className="text-xs text-gray-500" data-testid="chaser-sender-note">
            Sent from {senderEmail || "the firm's notifications mailbox"} · replies come back to you
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
            <Button onClick={handleSend} disabled={sending || !toEmail}
              className="bg-blue-600 text-white hover:bg-blue-500" data-testid="chaser-send">
              {sending ? "Sending..." : "Send Email"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Send Pay Link modal (BM-H163 — direct Stripe integration) ───────────
//
// May picks one outstanding invoice on this CC record, confirms the
// client's email and amount, reviews an editable email template
// (subject + HTML body carrying a {{PAY_LINK}} placeholder), and hits
// Send. The backend creates a Stripe Checkout Session on Bonsai Law's
// Stripe account, swaps the placeholder for the real Stripe URL, and
// sends the email as may@bonsai.law via Microsoft Graph (Sent Items
// visible in May's own mailbox).
function SendPayLinkModal({ open, onClose, onSent, record }) {
  const invoices = (record?.invoices || []).filter(
    inv => (inv.amount_outstanding || 0) > 0
  );
  const defaultInv = invoices[0] || null;

  const [invoiceNumber, setInvoiceNumber] = useState(defaultInv?.invoice_number || "");
  const [amountGbp, setAmountGbp] = useState(
    defaultInv ? Number(defaultInv.amount_outstanding).toFixed(2) : ""
  );
  const [clientEmail, setClientEmail] = useState(record?.client_email || "");
  const [sendEmail, setSendEmail] = useState(true);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [senderMailbox, setSenderMailbox] = useState("may@bonsai.law");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { pay_link, emailed_to_client }

  const loadDraft = async (invNum) => {
    if (!invNum || !record?.id) return;
    setPreviewLoading(true);
    try {
      const res = await axios.get(
        `${API}/credit-control/${record.id}/pay-link/preview`,
        { headers: headers(), params: { invoice_number: invNum } },
      );
      setSubject(res.data?.subject || "");
      setBodyHtml(res.data?.body_html || "");
      setSenderMailbox(res.data?.sender_mailbox || "may@bonsai.law");
    } catch (err) {
      // Non-blocking — user can still send with defaults or type their own.
      console.warn("pay-link preview failed:", err);
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const active = (record?.invoices || []).filter(
      inv => (inv.amount_outstanding || 0) > 0
    );
    const first = active[0] || null;
    setInvoiceNumber(first?.invoice_number || "");
    setAmountGbp(first ? Number(first.amount_outstanding).toFixed(2) : "");
    setClientEmail(record?.client_email || "");
    setSendEmail(true);
    setSubject("");
    setBodyHtml("");
    setError("");
    setResult(null);
    if (first?.invoice_number) loadDraft(first.invoice_number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, record]);

  const onInvoiceChange = (num) => {
    setInvoiceNumber(num);
    const inv = (record?.invoices || []).find(
      i => i.invoice_number === num
    );
    if (inv) setAmountGbp(Number(inv.amount_outstanding || 0).toFixed(2));
    loadDraft(num);
  };

  if (!open) return null;

  const handleSend = async () => {
    setError("");
    const amountFloat = Number(amountGbp);
    if (!invoiceNumber) return setError("Pick an invoice.");
    if (!Number.isFinite(amountFloat) || amountFloat <= 0)
      return setError("Enter an amount greater than zero.");
    if (!clientEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientEmail))
      return setError("Enter a valid client email.");
    if (sendEmail) {
      if (!subject.trim()) return setError("Subject cannot be blank when emailing the client.");
      if (!bodyHtml.trim()) return setError("Email body cannot be blank.");
      if (!/\{\{PAY_LINK\}\}/.test(bodyHtml)) {
        // Not a hard error — backend appends a fallback — but warn May.
        // Overwrite silently and let her confirm on second click if she really
        // wants to send without the token.
        const confirmed = window.confirm(
          "The {{PAY_LINK}} placeholder is missing from the body. The pay button will be appended at the bottom of the email. Send anyway?"
        );
        if (!confirmed) return;
      }
    }

    setSending(true);
    try {
      const res = await axios.post(
        `${API}/credit-control/${record.id}/pay-link`,
        {
          invoice_number: invoiceNumber,
          amount_pence: Math.round(amountFloat * 100),
          client_email: clientEmail.trim(),
          send_email: sendEmail,
          subject: subject.trim(),
          body_html: bodyHtml,
        },
        { headers: headers() },
      );
      setResult({
        pay_link: res.data?.pay_link || "",
        emailed_to_client: !!res.data?.emailed_to_client,
      });
      const emailed = !!res.data?.emailed_to_client;
      if (sendEmail && !emailed) {
        toast.warning(
          "Pay link created but the email did not send. Copy the link and send it manually."
        );
      } else {
        toast.success(
          emailed
            ? `Pay link emailed to the client from ${senderMailbox}`
            : "Pay link created"
        );
      }
      onSent && onSent();
    } catch (err) {
      const msg = errorMessage(err, "Could not create the pay link.");
      setError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    if (!result?.pay_link) return;
    try {
      await navigator.clipboard.writeText(result.pay_link);
      toast.success("Pay link copied");
    } catch {
      toast.error("Could not copy — please copy manually.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="send-pay-link-modal">
      <div className="bg-navy-card border border-white/10 rounded-xl p-6 w-full max-w-2xl my-8">
        <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
          <Link2 className="w-4 h-4 text-teal" /> Send Stripe Pay Link
        </h3>
        <p className="text-xs text-gray-400 mb-4">
          {record.client_name} — {record.matter_ref}
        </p>

        {result ? (
          <div className="space-y-3">
            <div className="bg-teal/10 border border-teal/30 rounded-lg p-3">
              <p className="text-sm text-teal font-medium mb-2">
                {result.emailed_to_client
                  ? `Emailed to ${clientEmail} from ${senderMailbox}. A copy is in that mailbox's Sent Items.`
                  : "Pay link ready — send it however you like."}
              </p>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={result.pay_link}
                  className="flex-1 bg-navy border border-white/10 rounded px-2 py-1.5 text-xs text-white truncate"
                  data-testid="pay-link-value"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyLink}
                  className="text-teal border-teal/30 text-xs"
                  data-testid="pay-link-copy"
                >
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                The link is valid for 24 hours. If it expires unused, send a fresh one.
              </p>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={onClose}
                className="bg-teal text-black hover:bg-teal-hover"
                data-testid="pay-link-done"
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Invoice</label>
                  <select
                    value={invoiceNumber}
                    onChange={e => onInvoiceChange(e.target.value)}
                    className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                    data-testid="pay-link-invoice"
                  >
                    {invoices.map(inv => (
                      <option key={inv.invoice_number} value={inv.invoice_number}>
                        {inv.invoice_number} — £{(inv.amount_outstanding || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Amount (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={amountGbp}
                    onChange={e => setAmountGbp(e.target.value)}
                    className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                    data-testid="pay-link-amount"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    Defaults to outstanding balance. Adjust for part-payments.
                  </p>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400 block mb-1">Client email</label>
                <input
                  type="email"
                  value={clientEmail}
                  onChange={e => setClientEmail(e.target.value)}
                  placeholder="client@example.com"
                  className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                  data-testid="pay-link-email"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={e => setSendEmail(e.target.checked)}
                  className="rounded"
                  data-testid="pay-link-send-email"
                />
                Email the pay link from {senderMailbox}
              </label>

              {sendEmail && (
                <div className="border-t border-white/10 pt-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-400">
                      Edit the subject and body. {"{{PAY_LINK}}"} is replaced with the Stripe URL.
                    </p>
                    {previewLoading && <span className="text-[11px] text-gray-500">Loading draft...</span>}
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Subject</label>
                    <input
                      type="text"
                      value={subject}
                      onChange={e => setSubject(e.target.value)}
                      className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                      data-testid="pay-link-subject"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Email body (HTML)</label>
                    <textarea
                      value={bodyHtml}
                      onChange={e => setBodyHtml(e.target.value)}
                      rows={14}
                      className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-xs text-white font-mono resize-y"
                      data-testid="pay-link-body"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">
                      Leave {"{{PAY_LINK}}"} in place where you want the payment button. Client will see rendered HTML, not raw tags.
                    </p>
                  </div>
                </div>
              )}

              {error && <p className="text-xs text-status-red">{error}</p>}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={onClose} className="text-gray-400" disabled={sending}>
                Cancel
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || invoices.length === 0}
                className="bg-teal text-black hover:bg-teal-hover"
                data-testid="pay-link-send"
              >
                {sending ? "Creating..." : sendEmail ? "Create and email link" : "Create link"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LogActivityModal({ open, onClose, onSaved, recordId }) {
  const [form, setForm] = useState({ type: "note", notes: "", outcome: "", promise_date: "" });
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.notes.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/credit-control/${recordId}/log-activity`, form, { headers: headers() });
      onSaved();
      onClose();
      setForm({ type: "note", notes: "", outcome: "", promise_date: "" });
    } catch (err) {
      console.error("Log activity failed:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="log-activity-modal">
      <div className="bg-navy-card border border-white/10 rounded-xl p-6 w-full max-w-lg">
        <h3 className="text-lg font-bold text-white mb-4">Log Activity</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Type</label>
            <select
              value={form.type}
              onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
              data-testid="activity-type"
            >
              <option value="call">Call</option>
              <option value="email">Email</option>
              <option value="note">Note</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              rows={4}
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white resize-none"
              placeholder="Activity details..."
              data-testid="activity-notes"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Promise date (optional)</label>
            <input
              type="date"
              value={form.promise_date}
              onChange={e => setForm(p => ({ ...p, promise_date: e.target.value }))}
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
              data-testid="activity-promise-date"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="ghost" onClick={onClose} className="text-gray-400">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !form.notes.trim()}
            className="bg-teal text-black hover:bg-teal-hover" data-testid="activity-save">
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── #181: Record Payment Modal ──────────────────────────────────────────────
// Brief 51 (13 Jul 2026) extends this modal with a ``Payment Source``
// dropdown. When the caller picks Client Money on A/C we fetch the
// matter's current MoA balance, cap the amount input at that balance,
// and echo the resulting remainder in the confirm copy. The POST body
// carries ``payment_source`` so the backend (see credit_control.py
// record_payment) can dual-write MoA + CC atomically.
// Issue #2964 (BM-H607): prefillFullBalance opens the form with the whole
// outstanding balance filled in. That is what Mark Paid now does, so a debt
// is only ever marked paid through a recorded payment.
function RecordPaymentModal({ open, onClose, onSaved, record, prefillFullBalance = false }) {
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    amount_paid: "",
    payment_date: today,
    invoice_id: "",
    notes: "",
    payment_source: "direct",
    // Brief 51.1 (14 Jul 2026) — Requested by initials. Required when
    // payment_source === "client_money_on_account" (that path drives a
    // MoA drawdown server-side); ignored otherwise. Defaults to the
    // matter's own fee earner initials on open; blank if the matter
    // FE is inactive or missing, in which case the caller MUST select.
    requested_by_initials: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [moaBalance, setMoaBalance] = useState(null);
  const [moaLoading, setMoaLoading] = useState(false);
  // Brief 51.1: active fee earners list for the Requested By dropdown.
  // Sourced from GET /api/hub/staff/fee-earners — session-authed, no
  // role gate. Server-side validation is the source of truth on submit.
  const [feeEarners, setFeeEarners] = useState([]);
  const [feeEarnersLoading, setFeeEarnersLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const defaultFe = ((record?.fee_earner_initials || record?.fee_earner || "") + "").trim().toUpperCase();
      const fullBalance = Number(record?.total_outstanding || 0);
      setForm({
        amount_paid: prefillFullBalance && fullBalance > 0 ? fullBalance.toFixed(2) : "",
        payment_date: today,
        invoice_id: "",
        notes: "",
        payment_source: "direct",
        // Brief 51.1: default to the matter's own fee earner initials.
        // Server validates against the active FE list on submit — if
        // the matter's FE has been deactivated, the caller sees the
        // validation error and picks another from the dropdown.
        requested_by_initials: defaultFe,
      });
    }
    setError("");
    setMoaBalance(null);
  }, [open, record, prefillFullBalance]);

  // Brief 51.1 (14 Jul 2026) — fetch the active fee-earners list once
  // on modal open. Kept outside the payment-source useEffect below so
  // the list is ready as soon as the caller flips to Client Money on
  // A/C. Silent no-op on error — the caller still sees the dropdown
  // (empty) plus the inline error state on submit.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFeeEarnersLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/hub/staff/fee-earners`, {
          headers: headers(),
        });
        if (!cancelled && Array.isArray(res.data)) setFeeEarners(res.data);
      } catch {
        // nosilent:brief51_1_fee_earners_fetch — swallowed on purpose;
        // submit-time server validation is the source of truth. The
        // caller sees an inline "requested_by_initials must be an
        // active fee earner" from the backend when the list fails to
        // load and they proceed with an empty selection.
      } finally {
        if (!cancelled) setFeeEarnersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Brief 51: fetch current MoA balance whenever the caller flips the
  // Payment Source dropdown to Client Money on A/C. We surface the
  // number in the modal so the caller can size the amount against what
  // is actually available before the server refuses on insufficient
  // funds. Network errors are surfaced inline (not fatal — user can
  // retry) rather than swallowed.
  useEffect(() => {
    let cancelled = false;
    if (!open || !record) return;
    if (form.payment_source !== "client_money_on_account") {
      setMoaBalance(null);
      return;
    }
    const matterRef = record.matter_ref || record.matter_reference || "";
    if (!matterRef) {
      setError("Cannot resolve matter reference; MoA balance lookup skipped.");
      setMoaBalance(0);
      return;
    }
    setMoaLoading(true);
    (async () => {
      try {
        const res = await axios.get(
          `${API}/money-on-account/summary/batch`,
          { params: { refs: matterRef }, headers: headers() }
        );
        if (cancelled) return;
        const rows = res?.data?.summaries || res?.data?.results || res?.data || [];
        const row = Array.isArray(rows)
          ? rows.find((r) => (r.matter_reference || r.matter_ref) === matterRef)
          : (rows[matterRef] || null);
        const balance = Number(
          (row && (row.money_on_account ?? row.balance ?? row.total)) || 0
        );
        setMoaBalance(Number.isFinite(balance) ? balance : 0);
      } catch (err) {
        if (cancelled) return;
        setMoaBalance(0);
        setError(
          errorMessage(err, "Could not load Client Money on A/C balance. Try Direct instead or retry.")
        );
      } finally {
        if (!cancelled) setMoaLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, record, form.payment_source]);

  if (!open || !record) return null;

  const invoices = record.invoices || [];
  const totalOutstanding = record.total_outstanding || 0;
  const amountPaid = parseFloat(form.amount_paid) || 0;
  const isFullPayment = amountPaid >= totalOutstanding && amountPaid > 0;
  const isMoaSource = form.payment_source === "client_money_on_account";
  const moaCap = isMoaSource && moaBalance !== null ? moaBalance : null;
  const amountOverBalance = moaCap !== null && amountPaid > moaCap + 1e-9;
  const remainingMoaAfter =
    moaCap !== null ? Math.max(0, Number((moaCap - amountPaid).toFixed(2))) : null;
  const targetInvoiceLabel =
    form.invoice_id ||
    (invoices
      .filter((inv) => (inv.amount_outstanding || 0) > 0)
      .sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"))[0]
      ?.invoice_number || "");

  const handleSave = async () => {
    if (!amountPaid || amountPaid <= 0) {
      setError("Amount must be greater than zero.");
      return;
    }
    if (amountOverBalance) {
      setError(
        `Insufficient balance: £${(moaCap || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })} available, ` +
        `£${amountPaid.toLocaleString("en-GB", { minimumFractionDigits: 2 })} requested.`
      );
      return;
    }
    // Brief 51.1 (14 Jul 2026) — Requested By is REQUIRED whenever the
    // payment_source is Client Money on A/C (that's the branch that
    // fires a MoA drawdown server-side). For direct payments the field
    // is not required — the record-payment path there does not touch
    // MoA. Server-side validation is the source of truth; the client
    // guard here is UX-only.
    if (isMoaSource && !(form.requested_by_initials || "").trim()) {
      setError("Requested by is required for Client Money on A/C drawdowns.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await axios.post(
        `${API}/credit-control/${record.id}/record-payment`,
        {
          amount_paid: amountPaid,
          payment_date: form.payment_date,
          invoice_id: form.invoice_id || undefined,
          notes: form.notes,
          payment_source: form.payment_source,
          // Brief 51.1 — pass through when set. Non-MoA callers still
          // POST the field (empty string) so a single payload shape
          // covers both branches.
          requested_by_initials: (form.requested_by_initials || "").trim().toUpperCase(),
        },
        { headers: headers() }
      );
      onSaved();
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Payment recording failed. Please try again."));
      console.error("Record payment failed:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="record-payment-modal">
      <div className="bg-navy-card border border-white/10 rounded-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-teal" />
          {prefillFullBalance ? "Mark Paid: record the payment" : "Record Payment"}
        </h3>
        <p className="text-xs text-gray-400 mb-1 font-mono">
          {record.client_name} — {record.matter_ref}
        </p>
        {record.status === "payment_arrangement" && (
          <p className="text-xs text-teal mb-3">Payment arrangement in place</p>
        )}

        <div className="bg-white/5 rounded-lg px-4 py-3 mb-4 flex items-center justify-between">
          <span className="text-xs text-gray-400">Currently outstanding</span>
          <span className="text-base font-bold text-teal">
            £{totalOutstanding.toLocaleString("en-GB", { minimumFractionDigits: 2 })}
          </span>
        </div>

        <div className="space-y-3">
          {/* Brief 51 (13 Jul 2026) — Payment Source dropdown. Sits ABOVE the
              amount input so the user picks the funding source before
              sizing the number. When Client Money on A/C is selected the
              amount cap slides down to the matter's MoA balance. */}
          <div>
            <label className="text-xs text-gray-400 block mb-1">Payment Source</label>
            <select
              value={form.payment_source}
              onChange={e => { setForm(p => ({ ...p, payment_source: e.target.value })); setError(""); }}
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
              data-testid="payment-source-select"
            >
              <option value="direct">Direct Payment</option>
              <option value="client_money_on_account">Client Money on A/C</option>
            </select>
            {isMoaSource && (
              <div className="mt-2 bg-teal/5 border border-teal/20 rounded px-3 py-2 flex items-center justify-between"
                data-testid="payment-source-moa-balance">
                <span className="text-xs text-gray-400">Available on account</span>
                <span className="text-sm font-bold text-teal">
                  {moaLoading
                    ? "Loading..."
                    : `\u00a3${(moaBalance || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`}
                </span>
              </div>
            )}
          </div>

          {/* Brief 51.1 (14 Jul 2026) — Requested by dropdown. Only
              shown when the payment source is Client Money on A/C —
              that's the branch that fires a MoA drawdown server-side.
              Defaults to the matter's own fee earner initials; the
              caller can pick another active FE. Server validates on
              submit and returns a 400 with the exact detail we surface
              inline. */}
          {isMoaSource && (
            <div data-testid="requested-by-block">
              <label className="text-xs text-gray-400 block mb-1">Requested by</label>
              <select
                value={form.requested_by_initials}
                onChange={e => { setForm(p => ({ ...p, requested_by_initials: e.target.value })); setError(""); }}
                className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                data-testid="requested-by-select"
              >
                <option value="">
                  {feeEarnersLoading ? "Loading fee earners..." : "— Select a fee earner —"}
                </option>
                {feeEarners.map(fe => (
                  <option key={fe.initials} value={fe.initials}>
                    {fe.initials}{fe.name ? ` — ${fe.name}` : ""}
                  </option>
                ))}
              </select>
              {!form.requested_by_initials && !feeEarnersLoading && (
                <p
                  className="text-[11px] text-status-amber mt-1"
                  data-testid="requested-by-hint"
                >
                  Required for Client Money on A/C drawdowns.
                </p>
              )}
            </div>
          )}

          <div>
            <label className="text-xs text-gray-400 block mb-1">Amount Received (\u00a3)</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={moaCap !== null ? moaCap : undefined}
              value={form.amount_paid}
              onChange={e => { setForm(p => ({ ...p, amount_paid: e.target.value })); setError(""); }}
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
              placeholder="0.00"
              data-testid="payment-amount"
              autoFocus
            />
            {amountPaid > 0 && (
              <p className={`text-xs mt-1 ${isFullPayment ? "text-teal" : "text-status-amber"}`}>
                {isFullPayment
                  ? "\u2713 Full payment \u2014 record will be marked as paid"
                  : `Part payment \u2014 \u00a3${(totalOutstanding - amountPaid).toLocaleString("en-GB", { minimumFractionDigits: 2 })} will remain outstanding`}
              </p>
            )}
            {isMoaSource && amountPaid > 0 && !amountOverBalance && moaBalance !== null && (
              <p className="text-xs mt-1 text-teal" data-testid="payment-source-confirm-copy">
                {`Apply \u00a3${amountPaid.toLocaleString("en-GB", { minimumFractionDigits: 2 })} from Client Money on A/C balance` +
                  (targetInvoiceLabel ? ` to invoice ${targetInvoiceLabel}.` : ".") +
                  ` Remaining balance after: \u00a3${(remainingMoaAfter || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}.`}
              </p>
            )}
            {isMoaSource && amountOverBalance && (
              <p className="text-xs mt-1 text-status-red" data-testid="payment-source-over-balance">
                {`Insufficient balance: \u00a3${(moaCap || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })} available, ` +
                  `\u00a3${amountPaid.toLocaleString("en-GB", { minimumFractionDigits: 2 })} requested.`}
              </p>
            )}
          </div>

          <div>
            <label className="text-xs text-gray-400 block mb-1">Payment Date</label>
            <input
              type="date"
              value={form.payment_date}
              onChange={e => setForm(p => ({ ...p, payment_date: e.target.value }))}
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
              data-testid="payment-date"
            />
          </div>

          {invoices.length > 1 && (
            <div>
              <label className="text-xs text-gray-400 block mb-1">Apply to invoice (optional)</label>
              <select
                value={form.invoice_id}
                onChange={e => setForm(p => ({ ...p, invoice_id: e.target.value }))}
                className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                data-testid="payment-invoice-select"
              >
                <option value="">Reduce oldest-first across all invoices</option>
                {invoices
                  .filter(inv => (inv.amount_outstanding || 0) > 0)
                  .map(inv => (
                    <option key={inv.id || inv.invoice_number} value={inv.invoice_number}>
                      {inv.invoice_number} — £{(inv.amount_outstanding || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                    </option>
                  ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-xs text-gray-400 block mb-1">Notes (optional)</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              rows={3}
              className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white resize-none"
              placeholder="e.g. BACS ref 20260413, part of payment arrangement"
              data-testid="payment-notes"
            />
          </div>

          {error && (
            <p className="text-xs text-status-red bg-status-red/10 rounded px-3 py-2" data-testid="payment-error">
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="ghost" onClick={onClose} className="text-gray-400" disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !form.amount_paid || parseFloat(form.amount_paid) <= 0 || amountOverBalance || (isMoaSource && moaLoading) || (isMoaSource && !(form.requested_by_initials || "").trim())}
            className={`text-white ${isFullPayment ? "bg-teal hover:bg-teal" : "bg-status-amber hover:bg-status-amber"}`}
            data-testid="payment-save"
          >
            {saving ? "Recording..." : isFullPayment ? "Mark as Paid" : "Record Part Payment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function CreditControlRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCallLog, setShowCallLog] = useState(false);
  const [showAddInvoice, setShowAddInvoice] = useState(false);
  const [showSendChaser, setShowSendChaser] = useState(false);
  const [showSendPayLink, setShowSendPayLink] = useState(false);
  const [showLogActivity, setShowLogActivity] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [recordFullBalance, setRecordFullBalance] = useState(false);
  // BM-H578: correction surface, shared with the Credit Control datagrid.
  const [showEdit, setShowEdit] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  // BM-H549 (16 Sep 2026): notify a colleague about a notepad note. Same
  // staff-picker contract as the attendance-note notify list.
  const [noteNotifyEnabled, setNoteNotifyEnabled] = useState(false);
  const [noteNotifyRecipients, setNoteNotifyRecipients] = useState([]);
  const [noteNotifyQuery, setNoteNotifyQuery] = useState("");
  const [matchForm, setMatchForm] = useState({ matter_ref: "", fee_earner: "" });
  const [matchSaving, setMatchSaving] = useState(false);

  const userObj2 = JSON.parse(localStorage.getItem("user") || "{}");
  const userEmail = (userObj2.user_email || userObj2.email || userObj2.ms_email || localStorage.getItem("bonsai_user_email") || "").toLowerCase().trim();
  const userRole = getUserRole(userEmail);
  const role = userRole === ROLES.ACCOUNTS ? "may"
    : userRole === ROLES.DIRECTOR || ["liezel@bonsai.law"].includes(userEmail) ? "director"
    : "fee_earner";
  const canWrite = role === "may" || role === "director";

  const fetchRecord = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/credit-control/${id}`, { headers: headers() });
      setRecord(res.data);
    } catch (err) {
      console.error("Fetch record failed:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchRecord(); }, [fetchRecord]);

  const doAction = async (action, body = {}) => {
    setActionLoading(action);
    try {
      await axios.post(`${API}/credit-control/${id}/${action}`, body, { headers: headers() });
      fetchRecord();
    } catch (err) {
      // BM-H68 — never silent again. Surface the real error so May can see
      // 4xx/5xx responses instead of clicking a dead button.
      console.error(`${action} failed:`, err);
      const detail = errorMessage(err, "Unknown error");
      const label = action.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
      alert(`${label} failed: ${detail}`);
    } finally {
      setActionLoading("");
    }
  };

  // BM-H69 — delete a CC record (soft-delete on the backend). May and
  // directors only; backend also refuses if there's a live outstanding
  // balance. Confirmation dialog names the matter so it's not a one-click
  // mistake.
  const deleteRecord = async () => {
    if (!record) return;
    const label = `${record.matter_ref || "this record"} (${record.client_name || "unknown client"})`;
    if (!window.confirm(`Delete ${label}?\n\nThe record will be removed from all credit control lists. This cannot be undone from the UI.`)) return;
    setActionLoading("delete");
    try {
      await axios.delete(`${API}/credit-control/${id}`, { headers: headers() });
      navigate("/credit-control");
    } catch (err) {
      console.error("delete failed:", err);
      const detail = errorMessage(err, "Unknown error");
      alert(`Delete failed: ${detail}`);
    } finally {
      setActionLoading("");
    }
  };

  // BM-H549 picker; BM-H589 removed the cap of 8 that hid Emily and Liezel.
  const noteNotifyOptions = useMemo(
    () => staffNotifyCandidates(noteNotifyQuery, noteNotifyRecipients),
    [noteNotifyQuery, noteNotifyRecipients],
  );

  const saveNote = async () => {
    if (!noteText.trim()) return;
    if (noteNotifyEnabled && noteNotifyRecipients.length === 0) {
      toast.error("Select at least one colleague to notify");
      return;
    }
    setSavingNote(true);
    try {
      const res = await axios.post(
        `${API}/credit-control/${id}/scratchpad`,
        { text: noteText, notify_emails: noteNotifyEnabled ? noteNotifyRecipients : [] },
        { headers: headers() },
      );
      setNoteText("");
      // BM-H549: the note is saved even when the notification fails, so say
      // which happened instead of a blanket success.
      const d = res?.data || {};
      if (d.notify_ok === true) toast.success(`Note saved. Notified ${d.notification_sent_to}`);
      else if (d.notify_ok === false) toast.error(`Note saved, but the notification failed: ${d.notify_reason || "unknown error"}`);
      else toast.success("Note saved");
      setNoteNotifyEnabled(false);
      setNoteNotifyRecipients([]);
      setNoteNotifyQuery("");
      fetchRecord();
    } catch (err) {
      console.error("Save note failed:", err);
      toast.error(`Save note failed: ${errorMessage(err, "Unknown error")}`);
    }
    finally { setSavingNote(false); }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/credit-control/${id}/scratchpad/${noteId}`, { headers: headers() });
      fetchRecord();
    } catch (err) { console.error("Delete note failed:", err); }
  };

  const referLitigation = async () => {
    const notes = prompt("Litigation referral notes (optional):");
    if (notes === null) return;
    setActionLoading("litigation");
    try {
      const res = await axios.post(`${API}/credit-control/${id}/refer-litigation`, { notes }, { headers: headers() });
      alert(`Litigation FORF created: ${res.data.matter_ref}`);
      fetchRecord();
    } catch (err) { console.error("Referral failed:", err); }
    finally { setActionLoading(""); }
  };

  const confirmMatch = async () => {
    if (!matchForm.matter_ref) return;
    setMatchSaving(true);
    try {
      await axios.patch(`${API}/credit-control/${id}`, {
        status: "active",
        matter_ref: matchForm.matter_ref,
        fee_earner: matchForm.fee_earner,
      }, { headers: headers() });
      fetchRecord();
    } catch (err) {
      console.error("Confirm match failed:", err);
    } finally {
      setMatchSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-navy flex items-center justify-center text-gray-500">Loading...</div>;
  if (!record) return <div className="min-h-screen bg-navy flex items-center justify-center text-gray-500">Record not found</div>;

  return (
    <div className="min-h-screen bg-navy text-white p-6" data-testid="cc-record-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/credit-control")} data-testid="cc-back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <button onClick={() => window.location.href="/"} className="mb-3 text-xs text-teal hover:text-teal flex items-center gap-1">← Dashboard</button>
          <h1 className="text-xl font-bold">{record.client_name}</h1>
          <p className="text-sm text-gray-400 font-mono">{record.matter_ref} — {record.fee_earner}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-teal">
            £{(record.total_outstanding || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
          </div>
          <div className="text-xs text-gray-400">{record.days_overdue} days overdue</div>
        </div>
      </div>

      {/* Pending Match Banner */}
      {record?.status === "pending_match" && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-4" data-testid="pending-match-banner">
          <h3 className="text-sm font-bold text-yellow-400 flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4" /> Awaiting matter match
          </h3>
          <p className="text-xs text-gray-300 mb-3">
            This invoice was imported but couldn't be automatically matched to a matter. Please link it to the correct matter.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Matter Reference</label>
              <input
                type="text"
                value={matchForm.matter_ref}
                onChange={e => setMatchForm(p => ({ ...p, matter_ref: e.target.value }))}
                placeholder="BL.VEC.006.0503"
                className="bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                data-testid="match-matter-ref"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Fee Earner</label>
              <select
                value={matchForm.fee_earner}
                onChange={e => setMatchForm(p => ({ ...p, fee_earner: e.target.value }))}
                className="bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white"
                data-testid="match-fee-earner"
              >
                <option value="">Select...</option>
                {FEE_EARNERS.map(fe => (
                  <option key={fe.initials} value={fe.initials}>{fe.name} ({fe.initials})</option>
                ))}
              </select>
            </div>
            <Button
              onClick={confirmMatch}
              disabled={matchSaving || !matchForm.matter_ref}
              className="bg-yellow-600 text-white hover:bg-yellow-500 text-xs"
              data-testid="match-confirm-btn"
            >
              {matchSaving ? "Saving..." : "Confirm Match"}
            </Button>
          </div>
        </div>
      )}

      {/* Escalation Tracker */}
      <div className="bg-navy-card border border-white/10 rounded-xl p-4 mb-4">
        <h3 className="text-xs text-gray-400 font-medium mb-2">ESCALATION PROGRESS</h3>
        <EscalationTracker currentStage={record.escalation_stage} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left column: Invoice Documents + Action Buttons */}
        <div className="space-y-4">
          {/* Invoice Documents */}
          <div className="bg-navy-card border border-white/10 rounded-xl p-4" data-testid="invoice-documents">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <FileText className="w-4 h-4 text-teal" /> Invoice Documents
              </h3>
              {canWrite && (
                <Button size="sm" variant="ghost" onClick={() => setShowAddInvoice(true)}
                  className="text-teal text-xs" data-testid="add-invoice-btn">
                  + Add
                </Button>
              )}
            </div>
            {(record.invoices || []).length === 0 ? (
              <p className="text-xs text-gray-500 py-2">No invoices attached</p>
            ) : (
              <div className="space-y-2">
                {record.invoices.map(inv => (
                  <div key={inv.id || inv.invoice_number} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-white flex items-center gap-2">
                        {inv.invoice_number}
                        {inv.origin === "lawbillity" && (
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] bg-teal/20 text-teal"
                            title="Manually entered from LawBillity"
                          >
                            LawBillity
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 flex items-center gap-2">
                        <span>Due: {inv.due_date || "—"}</span>
                        {inv.has_pdf && (
                          <a
                            href="#"
                            onClick={async (e) => {
                              e.preventDefault();
                              try {
                                const resp = await axios.get(
                                  `${API}/credit-control/${record.id}/invoices/${encodeURIComponent(inv.invoice_number)}/pdf`,
                                  { headers: headers() },
                                );
                                const { file_name, file_content_type, file_data } = resp.data || {};
                                if (!file_data) return;
                                const bin = atob(file_data);
                                const bytes = new Uint8Array(bin.length);
                                for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
                                const blob = new Blob([bytes], { type: file_content_type || "application/pdf" });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = file_name || `${inv.invoice_number}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                setTimeout(() => URL.revokeObjectURL(url), 60_000);
                              } catch (err) {
                                console.error("PDF download failed:", err);
                              }
                            }}
                            className="text-teal hover:underline inline-flex items-center gap-1"
                            data-testid={`inv-pdf-download-${inv.invoice_number}`}
                          >
                            <FileText className="w-3 h-3" />PDF
                          </a>
                        )}
                      </div>
                    </div>
                    <div className={`text-sm font-bold ${(inv.amount_outstanding || 0) <= 0 ? "text-gray-500 line-through" : "text-white"}`}>
                      £{(inv.amount_outstanding || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                ))}
                <div className="border-t border-white/10 pt-2 flex justify-between text-sm">
                  <span className="text-gray-400">Total outstanding</span>
                  <span className="font-bold text-teal">
                    £{(record.total_outstanding || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
            <div className="border-t border-white/10 mt-3 pt-3 flex justify-between items-center text-xs">
              <span className="text-gray-500">WIP (unbilled)</span>
              <span className="text-gray-400">
                {record.wip_value != null
                  ? <>£{record.wip_value.toLocaleString("en-GB", { minimumFractionDigits: 2 })} ({record.wip_hours}h unbilled) <span className="text-gray-600 ml-1">via LawBillity</span></>
                  : "—"}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          {canWrite && (
            <div className="bg-navy-card border border-white/10 rounded-xl p-4" data-testid="action-buttons">
              <h3 className="text-sm font-bold text-white mb-3">Actions</h3>
              <div className="grid grid-cols-2 gap-2">
                {/* BM-H578: correcting a mis-keyed amount, date or stage is a
                    different act from chasing, so it sits at the top of the
                    list and says what it is. */}
                <Button size="sm" variant="outline" onClick={() => setShowEdit(true)}
                  className="text-teal border-teal/30 justify-start text-xs col-span-2"
                  data-testid="action-edit-record">
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit Record
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowCallLog(true)}
                  className="text-white border-white/20 justify-start text-xs" data-testid="action-log-call">
                  <Phone className="w-3.5 h-3.5 mr-1.5" /> Log Call
                </Button>
                <Button size="sm" variant="outline" onClick={() => doAction("advance-stage")}
                  disabled={!!actionLoading}
                  className="text-white border-white/20 justify-start text-xs" data-testid="action-advance">
                  <ChevronRight className="w-3.5 h-3.5 mr-1.5" /> Advance Stage
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => { setRecordFullBalance(false); setShowRecordPayment(true); }}
                  disabled={!!actionLoading}
                  className="text-teal border-teal/30 justify-start text-xs col-span-2"
                  data-testid="action-record-payment">
                  <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Record Payment
                </Button>
                {/* Issue #2964 (BM-H607): Mark Paid posted to /credit-control/{id}/mark-paid,
                    a route that never existed, so it failed every time. It now opens
                    Record Payment with the full balance filled in: the payment is
                    recorded and the record turns paid when the balance reaches zero. */}
                <Button size="sm" variant="outline"
                  onClick={() => { setRecordFullBalance(true); setShowRecordPayment(true); }}
                  disabled={!!actionLoading || (record.total_outstanding || 0) <= 0}
                  className="text-teal border-teal/30 justify-start text-xs" data-testid="action-mark-paid">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark Paid
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => {
                    const notes = prompt("Dispute notes:");
                    if (notes) doAction("dispute", { dispute_notes: notes });
                  }}
                  disabled={!!actionLoading}
                  className="text-status-red border-red-500/30 justify-start text-xs" data-testid="action-dispute">
                  <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Dispute
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => {
                    const details = prompt("Payment arrangement details:");
                    if (details) doAction("payment-arrangement", { arrangement_details: details, agreed_amounts: [] });
                  }}
                  disabled={!!actionLoading}
                  className="text-teal border-teal/30 justify-start text-xs" data-testid="action-arrangement">
                  <Banknote className="w-3.5 h-3.5 mr-1.5" /> Arrangement
                </Button>
                {/* BM-H585 (Vanessa, 23 Sep 2026): accounts may write off as
                    well as directors; the directors are told when accounts does. */}
                {canWrite && (
                  <Button size="sm" variant="outline"
                    onClick={() => {
                      const reason = prompt("Write-off reason:");
                      if (reason) doAction("write-off", { reason });
                    }}
                    disabled={!!actionLoading}
                    className="text-gray-400 border-gray-500/30 justify-start text-xs" data-testid="action-write-off">
                    <XCircle className="w-3.5 h-3.5 mr-1.5" /> Write Off
                  </Button>
                )}
              </div>
              <h4 className="text-xs text-gray-400 font-medium mt-4 mb-2">Email & Documents</h4>
              <div className="grid grid-cols-2 gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowSendChaser(true)}
                  className="text-blue-400 border-blue-500/30 justify-start text-xs" data-testid="action-send-chaser">
                  <Mail className="w-3.5 h-3.5 mr-1.5" /> Send Chaser
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowSendPayLink(true)}
                  disabled={!!actionLoading || (record.total_outstanding || 0) <= 0}
                  className="text-teal border-teal/30 justify-start text-xs" data-testid="action-send-pay-link"
                  title="Create a Stripe pay link on bonsai.law and (optionally) email it to the client">
                  <Link2 className="w-3.5 h-3.5 mr-1.5" /> Send Pay Link
                </Button>
                <Button size="sm" variant="outline"
                  onClick={async () => {
                    setActionLoading("lba");
                    try {
                      const res = await axios.post(`${API}/credit-control/${record.id}/generate-lba`, {}, {
                        headers: headers(), responseType: "blob",
                      });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = `LBA_${record.matter_ref}_${new Date().toISOString().slice(0,10)}.docx`;
                      link.click();
                      window.URL.revokeObjectURL(url);
                      fetchRecord();
                    } catch (err) {
                      console.error("LBA generation failed:", err);
                      const detail = await parseBlobError(err);
                      alert(`LBA generation failed: ${detail}`);
                    }
                    finally { setActionLoading(""); }
                  }}
                  disabled={!!actionLoading}
                  className="text-gray-300 border-white/20 justify-start text-xs" data-testid="action-generate-lba">
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> {actionLoading === "lba" ? "Generating..." : "Generate LBA"}
                </Button>
              </div>
              {record.status !== "referred" && (
                <>
                  <h4 className="text-xs text-gray-400 font-medium mt-4 mb-2">Escalate</h4>
                  <Button size="sm" variant="outline" onClick={referLitigation}
                    disabled={!!actionLoading}
                    className="w-full text-purple-400 border-purple-500/30 justify-start text-xs" data-testid="action-refer-litigation">
                    <Scale className="w-3.5 h-3.5 mr-1.5" /> {actionLoading === "litigation" ? "Referring..." : "Refer to Litigation"}
                  </Button>
                </>
              )}
              {record.litigation_matter_ref && (
                <div className="mt-2 bg-purple-500/10 border border-purple-500/30 rounded-lg p-2">
                  <p className="text-xs text-purple-400">Referred: <span className="font-mono">{record.litigation_matter_ref}</span></p>
                </div>
              )}
              {/* BM-H69 — Delete record. May and directors only (backend gate). */}
              {canWrite && (
                <>
                  <h4 className="text-xs text-gray-400 font-medium mt-4 mb-2">Danger zone</h4>
                  <Button size="sm" variant="outline" onClick={deleteRecord}
                    disabled={!!actionLoading}
                    className="w-full text-red-400 border-red-500/30 hover:bg-red-500/10 justify-start text-xs" data-testid="action-delete-record">
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {actionLoading === "delete" ? "Deleting..." : "Delete Record"}
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Scratchpad */}
          <div className="bg-navy-card border border-white/10 rounded-xl p-4" data-testid="scratchpad-panel">
            <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-yellow-400" /> Notepad
            </h3>
            {canWrite && (
              <div className="mb-3">
                <textarea
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  rows={3}
                  className="w-full bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white resize-none placeholder:text-gray-500"
                  placeholder="Add a note..."
                  data-testid="scratchpad-input"
                />
                {/* BM-H549: notify a colleague, with a link straight back to
                    this note. Mirrors the attendance-note notify block. */}
                <div className="mt-2 rounded-md border border-white/10 bg-navy px-2 py-2" data-testid="scratchpad-notify-block">
                  <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={noteNotifyEnabled}
                      onChange={(e) => {
                        setNoteNotifyEnabled(e.target.checked);
                        if (!e.target.checked) { setNoteNotifyRecipients([]); setNoteNotifyQuery(""); }
                      }}
                      className="accent-yellow-500"
                      data-testid="scratchpad-notify-toggle"
                    />
                    <Bell className="w-3.5 h-3.5 text-yellow-400" /> Notify a colleague
                  </label>
                  {noteNotifyEnabled && (
                    <div className="mt-2">
                      {noteNotifyRecipients.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-1" data-testid="scratchpad-notify-chips">
                          {noteNotifyRecipients.map((em) => (
                            <span key={em} className="inline-flex items-center gap-1 rounded-full bg-yellow-600/20 border border-yellow-500/30 px-2 py-0.5 text-[10px] text-yellow-200">
                              {em.split("@")[0]}
                              <button
                                type="button"
                                onClick={() => setNoteNotifyRecipients(noteNotifyRecipients.filter((x) => x !== em))}
                                data-testid={`scratchpad-notify-remove-${em}`}
                                aria-label={`Remove ${em}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <input
                        type="text"
                        value={noteNotifyQuery}
                        onChange={(e) => setNoteNotifyQuery(e.target.value)}
                        placeholder="Search the team..."
                        className="w-full bg-navy-card border border-white/10 rounded px-2 py-1 text-xs text-white placeholder:text-gray-500"
                        data-testid="scratchpad-notify-input"
                      />
                      <div className="mt-1 max-h-32 overflow-y-auto" data-testid="scratchpad-notify-options">
                        {noteNotifyOptions.length === 0 ? (
                          <p className="text-[10px] text-gray-500 px-1 py-1">No matches</p>
                        ) : noteNotifyOptions.map((st) => (
                          <button
                            key={st.email}
                            type="button"
                            onClick={() => {
                              setNoteNotifyRecipients([...noteNotifyRecipients, st.email]);
                              setNoteNotifyQuery("");
                            }}
                            className="w-full text-left px-2 py-1 text-xs text-gray-300 hover:bg-white/5 rounded"
                            data-testid={`scratchpad-notify-option-${st.email}`}
                          >
                            {st.full_name} <span className="text-gray-500">{st.email}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <Button size="sm" onClick={saveNote} disabled={savingNote || !noteText.trim()}
                  className="mt-1 bg-yellow-600/80 text-white hover:bg-yellow-500 text-xs" data-testid="scratchpad-save">
                  {savingNote ? "Saving..." : "Save Note"}
                </Button>
              </div>
            )}
            {/* BM-H578: saved notes are no longer listed twice. They render in
                the Activity Log timeline with everything else that happened on
                this record, which is where May asked for them. */}
            <p className="text-xs text-gray-500 py-1" data-testid="scratchpad-timeline-pointer">
              {(record.scratchpad || []).length === 0
                ? "No notes yet. Saved notes appear in the Activity Log."
                : `${(record.scratchpad || []).length} note${(record.scratchpad || []).length === 1 ? "" : "s"} on this record, shown in the Activity Log.`}
            </p>
          </div>
        </div>

        {/* Middle column: Activity Log */}
        <div className="bg-navy-card border border-white/10 rounded-xl p-4" data-testid="activity-log-timeline">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Clock className="w-4 h-4 text-teal" /> Activity Log
            </h3>
            {canWrite && (
              <Button size="sm" variant="ghost" onClick={() => setShowLogActivity(true)}
                className="text-teal text-xs" data-testid="log-activity-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Log Activity
              </Button>
            )}
          </div>
          {(() => {
            const calls = (record.call_log || []).map(c => ({ ...c, _type: "call" }));
            const emails = (record.email_log || []).map(e => ({ ...e, _type: "email" }));
            const activities = (record.activity_log || []).map(a => ({ ...a, _type: a.type || "note" }));
            const payments = (record.payment_log || []).map(p => ({ ...p, _type: "payment" }));
            // BM-H578 (May Ann, 21 Sep 2026): "can we save the note in activity
            // log area instead below notes so its easier to see?" Notepad notes
            // now live in this one timeline alongside calls, emails and
            // payments, in date order, instead of in a separate list at the
            // bottom of the left column where nobody found them. The notepad
            // stays as the place you write one.
            const notes = (record.scratchpad || []).map(n => ({
              ...n, _type: "scratchpad_note", notes: n.text,
            }));
            // Sort by the correct timestamp field — each log type uses a different key
            const merged = [...calls, ...emails, ...activities, ...payments, ...notes]
              .sort((a, b) => {
                const ta = itemTimestamp(a);
                const tb = itemTimestamp(b);
                return tb.localeCompare(ta); // newest first
              });
            if (merged.length === 0) {
              return <p className="text-xs text-gray-500 py-4 text-center">No activity yet</p>;
            }
            const typeBadge = (type) => {
              const styles = {
                call: "bg-status-amber/20 text-status-amber",
                email: "bg-blue-500/20 text-blue-400",
                // BM-H546: the backend writes chaser sends as type
                // "chaser_sent", which had no badge, no label and no detail
                // line, so the log never said who the chaser went out from.
                chaser_sent: "bg-blue-500/20 text-blue-400",
                note: "bg-gray-500/20 text-gray-400",
                // BM-H578: a notepad note keeps the notepad's yellow so it is
                // still recognisable as May's own note in the timeline.
                scratchpad_note: "bg-yellow-500/20 text-yellow-400",
                payment: "bg-teal/20 text-teal",
                payment_arrangement: "bg-teal/20 text-teal",
                payment_received: "bg-teal/20 text-teal",
                dispute_flagged: "bg-status-red/20 text-status-red",
                write_off: "bg-gray-500/20 text-gray-400",
              };
              const labels = {
                call: "Call", email: "Email", chaser_sent: "Chaser",
                note: "Note", scratchpad_note: "Note", payment: "Payment",
                payment_arrangement: "Arrangement", payment_received: "Payment",
                dispute_flagged: "Dispute", write_off: "Write Off",
                // BM-H587 / BM-H585 Activity Log lines.
                category_change: "Category", owner_change: "Owner",
                hold_change: "Hold", hold_review_due: "Hold review",
                write_off_notified: "Write Off",
              };
              return (
                <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${styles[type] || styles.note}`}>
                  {labels[type] || type.replace(/_/g, " ")}
                </span>
              );
            };
            const borderColor = (type) =>
              type === "call" ? "border-orange-500/40"
              : (type === "email" || type === "chaser_sent") ? "border-blue-500/40"
              : (type === "payment" || type === "payment_received") ? "border-teal/40"
              : type === "payment_arrangement" ? "border-teal/40"
              : type === "dispute_flagged" ? "border-red-500/40"
              : type === "scratchpad_note" ? "border-yellow-500/40"
              : "border-gray-500/40";
            const displayTs = (item) => {
              const ts = itemTimestamp(item);
              if (!ts) return "";
              try {
                return new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
              } catch { return ts; }
            };
            return (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {merged.map((item, idx) => (
                  <div key={item.id || idx}
                    id={item._type === "scratchpad_note" ? `note-${item.id}` : undefined}
                    className={`border-l-2 ${borderColor(item._type)} pl-3 py-1 group scroll-mt-24`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {typeBadge(item._type)}
                        {item._type === "scratchpad_note" && item.author && (
                          <span className="text-xs text-gray-400">{item.author.split("@")[0]}</span>
                        )}
                        {item._type === "call" && item.call_number && (
                          <span className="text-xs font-bold text-white">#{item.call_number}</span>
                        )}
                        {(item._type === "email" || item._type === "chaser_sent") && item.template && (
                          <span className="text-xs font-bold text-white">
                            {item.template.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        )}
                        {(item._type === "payment" || item._type === "payment_received") && (
                          <span className="text-xs font-bold text-teal">
                            £{(item.amount_paid || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{displayTs(item)}</span>
                        {item._type === "scratchpad_note" && canWrite && (
                          <button onClick={() => deleteNote(item.id)}
                            className="text-gray-600 hover:text-status-red opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                            aria-label="Delete note"
                            data-testid={`scratchpad-delete-${item.id}`}>
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                    {item._type === "call" && item.outcome && (
                      <div className="mt-1">
                        <span className="inline-block px-2 py-0.5 rounded text-xs bg-status-amber/10 text-orange-300">
                          {item.outcome.replace(/_/g, " ")}
                        </span>
                      </div>
                    )}
                    {(item._type === "email" || item._type === "chaser_sent") && item.to_email && (
                      <p className="text-xs text-gray-400 mt-0.5">To: {item.to_email}</p>
                    )}
                    {/* BM-H546: which mailbox it left from, so "it isn't in my
                        Sent Items" is answerable from the record itself. */}
                    {item._type === "chaser_sent" && item.from_mailbox && (
                      <p className="text-xs text-gray-400 mt-0.5" data-testid="chaser-from-mailbox">
                        From: {item.from_mailbox}
                        {item.sender_fallback ? " (could not send as you)" : ""}
                      </p>
                    )}
                    {/* BM-H551: where a reply lands, shown when it is not the
                        sending mailbox — the case that used to send client
                        replies into an unmonitored mailbox. */}
                    {item._type === "chaser_sent" && item.reply_to &&
                      item.reply_to !== item.from_mailbox && (
                      <p className="text-xs text-gray-400 mt-0.5" data-testid="chaser-reply-to">
                        Replies to: {item.reply_to}
                      </p>
                    )}
                    {item._type === "email" && (
                      <div className="flex items-center gap-2 mt-1">
                        {item.sent_via_graph && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">Sent</span>
                        )}
                        {!item.sent_via_graph && item.to_email && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-status-amber/20 text-status-amber">Logged only</span>
                        )}
                      </div>
                    )}
                    {(item._type === "payment" || item._type === "payment_received") && item.invoice_id && (
                      <p className="text-xs text-gray-400 mt-0.5">Applied to: {item.invoice_id}</p>
                    )}
                    {(item._type === "payment" || item._type === "payment_received") && item.balance_after != null && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Balance after: £{item.balance_after.toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                      </p>
                    )}
                    {item.notes && (
                      <p className="text-xs text-gray-300 mt-1 whitespace-pre-wrap">{item.notes}</p>
                    )}
                    {item.promise_date && (
                      <p className="text-xs text-teal mt-1">Promise: {item.promise_date}</p>
                    )}
                    {item.pdf_generated && (
                      <span className="text-xs text-teal flex items-center gap-1 mt-1">
                        <FileText className="w-3 h-3" /> PDF generated
                      </span>
                    )}
                    {item._type === "scratchpad_note" && item.notified_to && (
                      <p className="text-[10px] text-gray-500 mt-0.5" data-testid={`scratchpad-notified-${item.id}`}>
                        Notified {item.notified_to}
                      </p>
                    )}
                    {item._type === "scratchpad_note" && item.notify_ok === false && (
                      <p className="text-[10px] text-status-red mt-0.5" data-testid={`scratchpad-notify-failed-${item.id}`}>
                        Notification failed{item.notify_reason ? `: ${item.notify_reason}` : ""}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Right column: Record Info */}
        <div className="space-y-4">
          <div className="bg-navy-card border border-white/10 rounded-xl p-4">
            <h3 className="text-sm font-bold text-white mb-3">Record Info</h3>
            <dl className="space-y-2 text-xs">
              {[
                ["Status", record.status?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())],
                ["Stage", record.escalation_stage?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())],
                ["Next Action", record.next_action?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "—"],
                ["Next Due", record.next_action_due || "—"],
                // BM-H587: untagged and unassigned are real answers, not blanks.
                ["Category", record.category_label || "Untagged"],
                ["Owner", record.owner_initials || "Unassigned"],
                ...(record.category === "hold"
                  ? [["Hold review by", record.hold_review_by || "—"], ["Hold reason", record.hold_reason || "—"]]
                  : []),
                ["Created", record.created_at ? new Date(record.created_at).toLocaleDateString("en-GB") : "—"],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between">
                  <dt className="text-gray-400">{label}</dt>
                  <dd className="text-white">{value}</dd>
                </div>
              ))}
            </dl>
          </div>

          {record.dispute_notes && (
            <div className="bg-status-red/10 border border-red-500/30 rounded-xl p-4">
              <h3 className="text-sm font-bold text-status-red mb-1">Dispute Notes</h3>
              <p className="text-xs text-gray-300">{record.dispute_notes}</p>
            </div>
          )}

          {record.payment_arrangement && (
            <div className="bg-teal/10 border border-teal/30 rounded-xl p-4">
              <h3 className="text-sm font-bold text-teal mb-2 flex items-center gap-2">
                <Banknote className="w-4 h-4" /> Payment Arrangement
              </h3>
              <p className="text-xs text-gray-300">{record.payment_arrangement.details}</p>
              {record.payment_arrangement.created_at && (
                <p className="text-xs text-gray-500 mt-1">
                  Set: {new Date(record.payment_arrangement.created_at).toLocaleDateString("en-GB")}
                  {record.payment_arrangement.created_by && ` by ${record.payment_arrangement.created_by.split("@")[0]}`}
                </p>
              )}
              {record.payment_arrangement.agreed_amounts?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {record.payment_arrangement.agreed_amounts.map((amt, i) => (
                    <div key={i} className="text-xs text-gray-400 flex justify-between">
                      <span>{amt.date || `Payment ${i + 1}`}</span>
                      <span className="text-white">£{(amt.amount || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
              )}
              {canWrite && (
                <Button size="sm" variant="outline"
                  onClick={() => { setRecordFullBalance(false); setShowRecordPayment(true); }}
                  className="mt-3 w-full text-teal border-teal/30 justify-center text-xs"
                  data-testid="arrangement-record-payment-btn">
                  <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Record Payment
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* BM-H551: every email tied to this matter, not only the ones sent from
          this screen. Reuses the matter email-audit panel so an invoice email,
          a statement or a CCL chase raised elsewhere is visible here too, and
          May never has to ask "did that actually go out?" again. */}
      {(record.matter_ref || record.matter_reference) && (
        <div className="mt-4" data-testid="credit-control-email-audit">
          <EmailDeliveryAudit matterReference={record.matter_ref || record.matter_reference} />
        </div>
      )}

      <CallLogModal open={showCallLog} onClose={() => setShowCallLog(false)} onSaved={fetchRecord} record={record} />
      <AddInvoiceModal open={showAddInvoice} onClose={() => setShowAddInvoice(false)} onSaved={fetchRecord} recordId={id} />
      <SendChaserModal open={showSendChaser} onClose={() => setShowSendChaser(false)} onSent={fetchRecord} record={record} />
      <SendPayLinkModal open={showSendPayLink} onClose={() => setShowSendPayLink(false)} onSent={fetchRecord} record={record} />
      <LogActivityModal open={showLogActivity} onClose={() => setShowLogActivity(false)} onSaved={fetchRecord} recordId={id} />
      <RecordPaymentModal open={showRecordPayment} onClose={() => setShowRecordPayment(false)} onSaved={fetchRecord} record={record} prefillFullBalance={recordFullBalance} />
      <EditCreditControlModal open={showEdit} recordId={id} record={record}
        onClose={() => setShowEdit(false)} onSaved={fetchRecord} />
    </div>
  );
}
